/**
 * Feature Toggle System with Infrastructure Dependencies
 *
 * Features can declare infrastructure dependencies, which drives:
 * - Which Docker services start (`docker-compose --profile`)
 * - Which tests run (Jest + Playwright auto-skip)
 * - Runtime validation (fail early if dependency missing)
 */

// ──────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────

export interface FeatureDependencies {
  /** Requires PostgreSQL database */
  database?: boolean
  /** Requires SES/MailHog email service */
  email?: boolean
  /** Requires S3 storage (via LocalStack or AWS) */
  storage?: boolean
  /** Requires LocalStack (S3, STS, IAM) */
  localstack?: boolean
  /** Requires OTEL collector for tracing/metrics */
  monitoring?: boolean
  /** External service URLs to validate (future use) */
  external?: string[]
}

export interface FeatureConfig {
  /** Whether the feature is enabled */
  enabled: boolean
  /** If true, feature is excluded in production */
  devOnly: boolean
  /** Human-readable description */
  description: string
  /** Infrastructure dependencies required by this feature */
  dependencies: FeatureDependencies
}

// ──────────────────────────────────────────────────────────
// Feature Definitions
// ──────────────────────────────────────────────────────────

export const features = {
  admin: {
    enabled: true,
    devOnly: false,
    description: 'Admin dashboard with user/role management',
    dependencies: { database: true },
  },
  profile: {
    enabled: true,
    devOnly: false,
    description: 'User profile page',
    dependencies: { database: true },
  },
  docs: {
    enabled: true,
    devOnly: false,
    description: 'Markdown documentation viewer',
    dependencies: {},
  },
  devtools: {
    enabled: true,
    devOnly: true,
    description: 'Developer tools for testing auth, email, and monitoring',
    dependencies: { email: true, localstack: true, monitoring: true },
  },
  about: {
    enabled: true,
    devOnly: false,
    description: 'Application information and diagnostics',
    dependencies: {},
  },
  entitlements: {
    enabled: true,
    devOnly: false,
    description: 'Entitlement management and seat allocation',
    dependencies: { database: true },
  },
  auth: {
    enabled: true,
    devOnly: false,
    description: 'Self-service registration and authentication',
    dependencies: { database: true },
  },
  files: {
    enabled: true,
    devOnly: false,
    description: 'File upload and download with user-scoped storage',
    dependencies: { storage: true },
  },
  notifications: {
    enabled: true,
    devOnly: false,
    description: 'In-app notification system',
    dependencies: { database: true },
  },
  jobs: {
    enabled: true,
    devOnly: false,
    description: 'Background job queue for async processing',
    dependencies: {},
  },
  invitations: {
    enabled: true,
    devOnly: false,
    description: 'Pre-auth gate invitation management for user onboarding',
    dependencies: { database: true },
  },
  import: {
    enabled: true,
    devOnly: false,
    description: 'AI-powered pipe model import from AFT Fathom PDFs',
    dependencies: { database: true, storage: true },
  },
  workspace: {
    enabled: true,
    devOnly: false,
    description: 'Project/model persistence with auto-save and workspace dashboard',
    dependencies: { database: true },
  },
  flowassist: {
    enabled: true,
    devOnly: false,
    description: 'AI-powered engineering assistant for hydraulic model Q&A',
    dependencies: {},
  },
  'ai-usage': {
    enabled: true,
    devOnly: false,
    description: 'AI usage tracking and cost reporting',
    dependencies: { database: true },
  },
  'pipe-usage': {
    enabled: true,
    devOnly: false,
    description: 'Usage-based subscription tracking: total pipes per customer over time',
    dependencies: { database: true },
  },
  'custom-templates': {
    enabled: true,
    devOnly: false,
    description: 'Tenant-scoped reusable model presets (FR-003 Tier 2)',
    dependencies: { database: true },
  },
  'pump-selection': {
    enabled: true,
    devOnly: false,
    description:
      'Live pump-selector microservice integration (catalog search, curve resolution)',
    dependencies: {
      database: true,
      external: ['PUMP_SELECTION_SERVICE_URL'],
    },
  },
  'device-library': {
    enabled: true,
    devOnly: false,
    description: 'User device library — reusable named device snapshots for model composition',
    dependencies: { database: true },
  },
  teams: {
    enabled: true,
    devOnly: false,
    description: 'Team (user group) management — tenant-scoped teams for access sharing',
    dependencies: { database: true },
  },
  // Ships dark. Bundles carry customer engineering IP to a receiver that is
  // dev-tier for the pilot, so the rollout is internal tenants first and the
  // flag is the off switch — enable per deployment via FEATURE_FLAGS.
  support_bundles: {
    enabled: false,
    devOnly: false,
    description: 'Ctrl+Shift+B diagnostics bundle — screenshot, logs, and the open model',
    dependencies: {},
  },
  collaboration: {
    enabled: false, // default-off; a per-deployment FEATURE_FLAGS override turns it on
    devOnly: false,
    description: 'Model/project sharing & collaboration',
    dependencies: { database: true },
  },
  overtime: {
    enabled: false, // default-off; a per-deployment FEATURE_FLAGS override turns it on
    // NOT devOnly. devOnly means local-only — no deployed environment, dev
    // included — because NODE_ENV is 'production' everywhere deployed. Setting it
    // true would make OverTime impossible to exercise on the dev deployment,
    // which is where it gets tested. See spec §2.4.
    devOnly: false,
    description: 'Time-based (extended-period) simulation with rules and playback',
    dependencies: { database: true, storage: true },
  },
  model_history: {
    enabled: false, // default-off (shadow mode); a per-deployment FEATURE_FLAGS override turns it on
    devOnly: false,
    description: 'Model change-log history — reserved for D4 live-read; does NOT gate versioning (the change-log substrate is always-on)',
    dependencies: { database: true },
  },
} as const satisfies Record<string, FeatureConfig>

export type FeatureName = keyof typeof features

export type FeatureOverrides = Partial<Record<FeatureName, boolean>>

// ──────────────────────────────────────────────────────────
// Feature State Queries
// ──────────────────────────────────────────────────────────

/**
 * Check if a feature is enabled. A per-deployment override (from FEATURE_FLAGS)
 * takes precedence over the static default; the devOnly-in-production guard still
 * applies on top of an override.
 */
export function isFeatureEnabled(
  name: FeatureName,
  isProduction = false,
  overrides?: FeatureOverrides,
): boolean {
  const feature = features[name]
  if (!feature) return false
  const enabled = overrides?.[name] ?? feature.enabled
  if (!enabled) return false
  if (feature.devOnly && isProduction) return false
  return true
}

/**
 * Parse the FEATURE_FLAGS env (a JSON object of { [featureName]: boolean }) into a
 * validated override map. Fail-closed: absent/malformed/non-object/array -> {}; only
 * known feature names with strict boolean values are kept.
 */
export function parseFeatureFlags(raw: string | undefined): FeatureOverrides {
  if (!raw) return {}
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return {}
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {}
  const result: FeatureOverrides = {}
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (!Object.prototype.hasOwnProperty.call(features, key)) continue
    if (value !== true && value !== false) continue
    result[key as FeatureName] = value
  }
  return result
}

/**
 * Check if a feature is development-only
 */
export function isDevFeature(name: FeatureName): boolean {
  return features[name]?.devOnly ?? false
}

/** Runtime environment names that are NOT a stg/prd deployment. */
const DEV_ENVIRONMENTS = ['dev', 'development', 'local']

/**
 * Whether a runtime environment name (the ENVIRONMENT var) is a non-production one.
 *
 * Used by the backend feature-gate as a *belt* on its NODE_ENV check, not as the primary
 * signal: dev-only features are local-only, and NODE_ENV already separates local from every
 * deployed environment. This exists so that a deployed task with NODE_ENV somehow missing
 * still can't mount DevTools in front of a customer.
 *
 * Whitelist, not blocklist — an absent or unrecognized name is NOT a dev environment, so
 * the gate fails closed on a typo or a newly-named environment.
 */
export function isDevEnvironmentName(environment: string | null | undefined): boolean {
  if (!environment) return false
  return DEV_ENVIRONMENTS.includes(environment)
}

/**
 * Get list of enabled feature names
 */
export function getEnabledFeatureNames(isProduction = false): FeatureName[] {
  return (Object.keys(features) as FeatureName[]).filter((name) =>
    isFeatureEnabled(name, isProduction)
  )
}

// ──────────────────────────────────────────────────────────
// Infrastructure Dependency Resolution
// ──────────────────────────────────────────────────────────

/**
 * Compute the set of infrastructure dependencies required by all enabled features.
 * Used to determine which Docker services to start and which env vars are required.
 */
export function getRequiredInfrastructure(
  isProduction = false
): Set<keyof FeatureDependencies> {
  const required = new Set<keyof FeatureDependencies>()

  for (const name of getEnabledFeatureNames(isProduction)) {
    const deps = features[name].dependencies
    // Use 'in' operator to safely check properties on narrowed const types
    if ('database' in deps && deps.database) required.add('database')
    if ('email' in deps && deps.email) required.add('email')
    if ('storage' in deps && deps.storage) required.add('storage')
    if ('localstack' in deps && deps.localstack) required.add('localstack')
    if ('monitoring' in deps && deps.monitoring) required.add('monitoring')
  }

  return required
}

/**
 * Get the dependencies for a specific feature
 */
export function getFeatureDependencies(name: FeatureName): FeatureDependencies {
  return features[name]?.dependencies ?? {}
}
